import React, { useState, useRef } from 'react';
import api from '../lib/api';
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export default function ImportExportPage() {
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const res = await api.importCustomers(file);
      setImportResult({ success: true, imported: res.data.imported, errors: res.data.errors });
    } catch (err) {
      setImportResult({ success: false, message: err.response?.data?.detail || 'Import failed' });
    }
    setImporting(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.exportCustomers();
      const data = res.data;
      if (!data.length) {
        alert('No subscribers to export');
        setExporting(false);
        return;
      }
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Subscribers');
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `GrabMillet_Customers_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      alert('Export failed');
      console.error(err);
    }
    setExporting(false);
  };

  const handleDownloadTemplate = () => {
    const template = [
      { 'Name': 'John Doe', 'Mobile No': '9876543210', 'Apartment Name': 'Aspire Heights', 'Language': 'Telugu, English' }
    ];
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'GrabMillet_Import_Template.xlsx');
  };

  return (
    <div data-testid="import-export-panel" className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>Import / Export</h2>
        <p className="text-sm mt-1" style={{ color: '#A8A29E' }}>Manage customer data via Excel files</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Import */}
        <div className="rounded-xl p-6" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(96,165,250,0.1)' }}>
              <Upload className="w-5 h-5" style={{ color: '#60A5FA' }} />
            </div>
            <div>
              <h3 className="font-semibold" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>Import Customers</h3>
              <p className="text-xs" style={{ color: '#78716C' }}>Upload an Excel file (.xlsx)</p>
            </div>
          </div>

          <div className="rounded-lg p-4 mb-4 text-sm" style={{ background: '#100E0C', border: '1px dashed #2A241F' }}>
            <p className="mb-2" style={{ color: '#A8A29E' }}>Expected columns:</p>
            <div className="flex flex-wrap gap-2">
              {['Name', 'Mobile No', 'Apartment Name', 'Language'].map(col => (
                <span key={col} className="px-2 py-0.5 rounded text-xs font-medium" style={{ background: 'rgba(200,169,110,0.1)', color: '#C8A96E', border: '1px solid rgba(200,169,110,0.2)' }}>{col}</span>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <label data-testid="import-file-button" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold cursor-pointer transition-all" style={{ background: '#C8A96E', color: '#000' }}>
              <Upload className="w-4 h-4" />
              {importing ? 'Importing...' : 'Choose File & Import'}
              <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleImport} className="hidden" />
            </label>
            <button data-testid="download-template-button" onClick={handleDownloadTemplate} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all" style={{ background: '#2A241F', color: '#C8A96E', border: '1px solid rgba(200,169,110,0.2)' }}>
              <FileSpreadsheet className="w-4 h-4" /> Download Template
            </button>
          </div>

          {importResult && (
            <div className="mt-4 rounded-lg p-3 text-sm" style={{
              background: importResult.success ? 'rgba(74,222,128,0.1)' : 'rgba(248,113,113,0.1)',
              border: `1px solid ${importResult.success ? 'rgba(74,222,128,0.2)' : 'rgba(248,113,113,0.2)'}`,
              color: importResult.success ? '#4ADE80' : '#F87171'
            }}>
              {importResult.success ? (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Successfully imported {importResult.imported} customers</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  <span>{importResult.message}</span>
                </div>
              )}
              {importResult.errors?.length > 0 && (
                <div className="mt-2 text-xs" style={{ color: '#FBBF24' }}>
                  {importResult.errors.map((e, i) => <p key={i}>{e}</p>)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Export */}
        <div className="rounded-xl p-6" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(74,222,128,0.1)' }}>
              <Download className="w-5 h-5" style={{ color: '#4ADE80' }} />
            </div>
            <div>
              <h3 className="font-semibold" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>Export Customers</h3>
              <p className="text-xs" style={{ color: '#78716C' }}>Download all subscribers as Excel</p>
            </div>
          </div>

          <div className="rounded-lg p-4 mb-4 text-sm" style={{ background: '#100E0C', border: '1px dashed #2A241F' }}>
            <p style={{ color: '#A8A29E' }}>Exported columns include: Name, Mobile No, Apartment, Flat, Area, Plan, Status, Delivery Slot, Days Left, Quantity, Items, Languages, Bottle Deposit, Remarks</p>
          </div>

          <button data-testid="export-button" onClick={handleExport} disabled={exporting} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all" style={{ background: '#C8A96E', color: '#000' }}>
            <Download className="w-4 h-4" /> {exporting ? 'Exporting...' : 'Export to Excel'}
          </button>
        </div>
      </div>
    </div>
  );
}
